import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { KeyBadge } from '@/components/ui/cifras/key-badge';
import {
  EmptyState,
  InfiniteScrollSentinel,
  ListSearch,
  ListSummary,
} from '@/components/ui/cifras/list-toolbar';
import {
  IconTile,
  ResourceCard,
  ResourceCardGrid,
} from '@/components/ui/cifras/resource-card';
import { Content } from '@/components/ui/content';
import { ActionsButton } from '@/components/ui/data-table';
import { Icon } from '@/components/ui/icon';
import { Segmented } from '@/components/ui/segmented';
import { useRouter } from '@/hooks/use-router';
import { date } from '@/lib/date';
import { meta } from '@/lib/meta';
import { usePermissions } from '@/services/auth';
import { setlistKeys, useDeleteSetlist, useInfiniteSetlists } from '@/services/setlists';

export const Route = createFileRoute('/_app/setlists/')({
  head: () => meta({ label: 'Repertórios' }),
  component: RouteComponent,
});

type Scope = 'all' | 'mine' | 'upcoming';

const model = 'setlists';
const PER_PAGE = 24;

/** Código curto do repertório (UUIDv7 começa igual para ids próximos, então usa o fim). */
const shortCode = (id: string) => `#${id.slice(-6)}`;

function RouteComponent() {
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { canCreate, canUpdate, canDelete } = createPermissions(model);
  const { mutateAsync: remove } = useDeleteSetlist();

  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<Scope>('all');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteSetlists({
    search: search || undefined,
    mine: scope === 'mine' ? 'true' : undefined,
    upcoming: scope === 'upcoming' ? 'true' : undefined,
    orderBy: scope === 'upcoming' ? 'scheduledAt' : 'createdAt',
    order: scope === 'upcoming' ? 'asc' : 'desc',
    perPage: PER_PAGE,
  });

  const setlists = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.pagination.total ?? 0;

  return (
    <Content
      breadcrumbs={{
        icon: 'list-music',
        title: 'Repertórios',
        action: canCreate && (
          <Button variant="success" onClick={() => push('/setlists/create')} icon="plus">
            Novo repertório
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <ListSearch
            className="min-w-[260px] flex-1"
            placeholder="Buscar repertório ou responsável..."
            onSearch={(term) => {
              setSearch(term);
            }}
          />
          <Segmented<Scope>
            value={scope}
            onChange={(value) => {
              setScope(value);
            }}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'mine', label: 'Meus' },
              { value: 'upcoming', label: 'Próximos' },
            ]}
          />
        </div>

        {data && <ListSummary shown={setlists.length} total={total} noun="repertórios" />}

        {data && setlists.length === 0 ? (
          <EmptyState
            title="Nenhum repertório encontrado"
            hint={scope !== 'all' || search ? 'Tente outro filtro ou termo.' : undefined}
          />
        ) : (
          <ResourceCardGrid>
            {setlists.map((setlist) => (
              <ResourceCard
                key={setlist.id}
                muted={setlist.isActive === false}
                leading={
                  <IconTile>
                    <Icon name="list-music" className="size-[18px]" />
                  </IconTile>
                }
                title={setlist.name}
                subtitle={
                  <>
                    por {setlist.user?.name ?? '—'}
                    {setlist.scheduledAt &&
                      ` · ${date(setlist.scheduledAt as unknown as string).format('DD/MM/YYYY HH:mm')}`}
                  </>
                }
                trailing={
                  <div className="flex items-center gap-2">
                    {setlist.isActive === false && (
                      <Badge variant="destructive">Inativo</Badge>
                    )}
                    <span className="font-mono text-[12px] text-faint">
                      {shortCode(setlist.id)}
                    </span>
                  </div>
                }
                preview={
                  <ol className="flex flex-col gap-1.5">
                    {setlist.preview.map((item) => (
                      <li
                        key={item.order}
                        className="flex items-center gap-2.5 text-[13px]"
                      >
                        <span className="w-4 text-right font-bold text-accent-ink">
                          {item.order + 1}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          {item.title}
                        </span>
                        <KeyBadge value={item.key} className="h-5 text-[11px]" />
                      </li>
                    ))}
                    {setlist.songCount === 0 && (
                      <li className="text-[13px] text-faint">Nenhuma música ainda</li>
                    )}
                    {setlist.songCount > 3 && (
                      <li className="pl-[26px] text-[12px] text-muted-foreground">
                        + {setlist.songCount - 3} músicas
                      </li>
                    )}
                  </ol>
                }
                actions={
                  <>
                    <Button
                      size="sm"
                      variant="sand"
                      onClick={() => push(`/setlists/${setlist.id}/view`)}
                    >
                      <Icon name="play" />
                      Palco
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      aria-label="Imprimir"
                      onClick={() => push(`/setlists/${setlist.id}/print`)}
                    >
                      <Icon name="printer" />
                    </Button>
                    <ActionsButton
                      id={setlist.id}
                      edit={canUpdate}
                      remove={canDelete}
                      onEdit={(id) => push(`/setlists/${id}`)}
                      onRemove={(id) => remove({ id })}
                      queryKey={setlistKeys.all}
                    />
                  </>
                }
                onClick={() => push(`/setlists/${setlist.id}/view`)}
              />
            ))}
          </ResourceCardGrid>
        )}

        <InfiniteScrollSentinel
          hasNextPage={Boolean(hasNextPage)}
          isFetching={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </div>
    </Content>
  );
}
